// @ts-ignore
import CryptoJS from "crypto-js";

const HYDRA_ENCRYPTION_KEY = "4s36MTN1baT77E3";

/**
 * Decrypts an MSISDN from a Hydra UUID parameter
 * Uses RC4-40-BASE64ENCODE encryption
 *
 * Returns null on any failure and never logs. The encrypted uuid exists so the
 * subscriber's number is not handled in the clear on the client, and the browser
 * console is not a private place — do not log the input or the result here. The
 * caller reports the outcome instead (see useHydraUuidLogin).
 *
 * @param encryptedValue - BASE64 encoded encrypted MSISDN from Hydra
 * @returns Decrypted MSISDN string or null if decryption fails
 */
export function decryptMsisdnFromUuid(encryptedValue: string): string | null {
  try {
    // Validate input
    if (!encryptedValue || typeof encryptedValue !== "string") {
      return null;
    }

    // Decrypt using RC4 with raw key bytes (WordArray).
    // Passing a string key to crypto-js causes it to apply an MD5-based KDF,
    // which produces the wrong key. We must parse the key as raw UTF-8 bytes.
    const rawKey = CryptoJS.enc.Utf8.parse(HYDRA_ENCRYPTION_KEY);
    const cipherParams = CryptoJS.lib.CipherParams.create({
      ciphertext: CryptoJS.enc.Base64.parse(encryptedValue),
    });
    const decrypted = CryptoJS.RC4.decrypt(cipherParams, rawKey);

    // Get UTF-8 string from decrypted data
    const msisdn = decrypted.toString(CryptoJS.enc.Utf8);

    // Validate result is not empty and contains only digits
    if (!msisdn || !/^\d+$/.test(msisdn)) {
      return null;
    }

    return msisdn;
  } catch {
    // Silent fail - don't expose decryption errors
    return null;
  }
}
